import i18next from 'i18next';

/**
 * Reading and writing numbers the way the reader's locale does — kept apart from the input component so
 * the parsing can be tested without pulling a React tree (and the `@/` alias) in with it.
 */

export type NumericKind = 'money' | 'decimal' | 'percent' | 'integer';

/** Decimals each kind is rounded and displayed to. Money is always exactly two. */
export const DECIMALS: Record<NumericKind, number> = { money: 2, decimal: 2, percent: 1, integer: 0 };

/** Only money is grouped: thousands in a ratio or a percentage are noise, in a price they are not. */
export const GROUPED: Record<NumericKind, boolean> = {
    money: true,
    decimal: false,
    percent: false,
    integer: true,
};

/** The language in force. Separators are a property of the reader, not of the field. */
export const currentLocale = (): string => i18next.resolvedLanguage || i18next.language || 'de';

/** The character this locale puts between the whole part and the fraction. */
export const decimalSeparatorOf = (locale: string): string =>
    new Intl.NumberFormat(locale)
        .formatToParts(1.1)
        .find((part) => part.type === 'decimal')?.value ?? ',';

export const displayFormat = (kind: NumericKind, locale: string = currentLocale()) =>
    new Intl.NumberFormat(locale, {
        minimumFractionDigits: kind === 'money' ? 2 : 0,
        maximumFractionDigits: DECIMALS[kind],
        useGrouping: GROUPED[kind],
    });

/**
 * Read a typed number.
 *
 * German writes 1.234,56 and English 1,234.56, and people type both. The rule that settles it: of
 * the two separators, whichever appears **last** is the decimal point and the other is grouping.
 * That reads both notations without having to know which one was meant.
 *
 * With only one separator there is a genuine ambiguity, and the locale settles it: a lone separator
 * that *is* this locale's decimal character is read as one. Otherwise the digits after it decide —
 * one or two make it a decimal point (`12.5` is twelve and a half), three or more make it grouping
 * (`1.234` is one thousand).
 *
 * A minus is dropped rather than rejected: these are areas, prices and rates, and there is no
 * meaning to a negative one. Silently taking the magnitude beats refusing the keystroke, which
 * looks like a broken keyboard.
 */
export const parseNumeric = (text: string, locale: string = currentLocale()): number | null => {
    const trimmed = String(text ?? '').replace(/[\s€%]/g, '').replace(/-/g, '');
    if (trimmed.length === 0) return null;

    const lastComma = trimmed.lastIndexOf(',');
    const lastDot = trimmed.lastIndexOf('.');

    let normalized: string;
    if (lastComma >= 0 && lastDot >= 0) {
        const decimal = lastComma > lastDot ? ',' : '.';
        const grouping = decimal === ',' ? '.' : ',';
        normalized = trimmed.split(grouping).join('').replace(decimal, '.');
    } else {
        const only = lastComma >= 0 ? ',' : lastDot >= 0 ? '.' : null;
        const decimal = decimalSeparatorOf(locale);

        if (only === null) {
            normalized = trimmed;
        } else if (only === decimal || new RegExp(`^\\d+\\${only}\\d{1,2}$`).test(trimmed)) {
            normalized = trimmed.replace(only, '.');
        } else {
            normalized = trimmed.split(only).join('');
        }
    }

    const value = Number(normalized);
    return Number.isFinite(value) ? Math.abs(value) : null;
};

export const round = (value: number, decimals: number): number => {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
};

/** The editable form: the value without grouping, with a comma, as a German keyboard produces it. */
export const toEditText = (
    value: number | null,
    kind: NumericKind,
    locale: string = currentLocale(),
): string =>
    value === null || value === undefined
        ? ''
        : new Intl.NumberFormat(locale, {
              maximumFractionDigits: DECIMALS[kind],
              useGrouping: false,
          }).format(value);
